// Inspection-progress helpers — derives per-team rollups from the per-robot
// queue + persistent rollup state. Used by the status pill to render
// "At Inspection · X/Y" when a team has multiple robots in flight.

import type { AppState, MatchEvent, QueueItem, TeamId } from '../types';
import { ROBOTS_BY_EVENT } from '../types';
import { teamById } from '../mock/data';

/**
 * Whether a queue item is "urgent" — its match is imminent enough that
 * inspectors should prioritize it. Derived dynamically from the current
 * match list (live pointer + match index). Replaces the static `urgent`
 * flag on QueueItem, which was set at insert time and never updated as
 * the schedule progressed.
 *
 * Formula: a queue item is urgent if its match is within two slots of the
 * LIVE match. We deliberately don't reference the Up-Next (formerly
 * "on-deck") slot because that pointer only advances once a later match's
 * teams clear inspection — if G3 clears inspection before G2 does, the
 * Up-Next slot snaps to G3 even though the queue still has unfinished
 * G2/G4/G5 work to triage. Live is the stable reference.
 *
 *   live == G1 → matches G2 and G3 are urgent.
 *
 * Cold-start fallback: an event may have queue entries before any of its
 * matches goes live (typical when 3v3 is running and 1v1 hasn't started
 * — early E* teams are getting inspected even though no E* match is
 * live yet). If no match is live, we anchor on the first not-yet-finished
 * match instead, so SOON still fires for the imminent matches.
 *
 * With the dynamic compute, both red and blue teams of the same match
 * always agree on urgency — the static flag could drift between sides if
 * their queue rows were inserted at different times.
 *
 * Returns false if the queue item's match isn't in the live match list
 * (defensive — shouldn't happen if invariants hold).
 */
export const SOON_WINDOW_MATCHES = 2;

const FINISHED_STATUSES = new Set(['completed', 'forfeit_v', 'forfeit_iv', 'rescheduled', 'direct_transfer']);

export function isQueueItemUrgent(item: QueueItem, state: AppState): boolean {
  const matches = item.event === '3v3' ? state.matches3v3 : state.matches1v1;
  const liveIdx = matches.findIndex((m) => m.live);
  const refIdx = liveIdx >= 0
    ? liveIdx
    // No live match — use the first not-yet-finished match as the anchor.
    // Both sides finished = match is done; otherwise it's still pending and
    // is effectively "the next thing to happen on this field".
    : matches.findIndex((m) => !FINISHED_STATUSES.has(m.redS) || !FINISHED_STATUSES.has(m.blueS));
  const matchIdx = matches.findIndex((m) => m.id === item.match);
  return refIdx >= 0 && matchIdx >= 0 && matchIdx - refIdx <= SOON_WINDOW_MATCHES;
}

export interface InspectionProgress {
  /** Robots that have cleared all 3 inspection stations. */
  passed: number;
  /** Total robots the team brings to this event (1 for 1v1, up to 3 for 3v3). */
  total: number;
}

/**
 * Compute (passed, total) inspection progress for a (team, match) pair.
 * `passed` counts robots whose queue rows have cleared all three stations
 * (passedStations covers s1+s2+s3) — equivalently, robots at L3 in the
 * teamRobotInspection rollup. `total` is the team's event-relevant robot
 * count.
 *
 * If the team has no queue rows for this match (already past inspection or
 * not yet entered), falls back to the teamRobotInspection rollup so a
 * team currently at L3 / In Game still reports a sensible count.
 */
export function inspectionProgressFor(
  team: TeamId,
  match: string,
  event: MatchEvent,
  state: AppState,
): InspectionProgress {
  const eventClasses = ROBOTS_BY_EVENT[event];
  const teamRobots = teamById(team).robots.filter((r) => eventClasses.includes(r));
  const total = teamRobots.length;
  if (total === 0) return { passed: 0, total: 0 };

  const queueRows = state.queue.filter(
    (q) => q.team === team && q.match === match && q.event === event,
  );
  if (queueRows.length > 0) {
    // Denominator stays at the team's main roster size — backup robots
    // and skipped rows aren't part of the lineup. So a team with backup
    // shows "3/3" the moment all 3 main robots clear, regardless of
    // backup state. A team with a main robot skipped shows "2/3", which
    // tells ops the lineup is short and an override may be needed.
    const mainRows = queueRows.filter((q) => !q.isBackup && !q.skipped);
    const passed = mainRows.filter((q) => q.passedStations.length === 3).length;
    return { passed, total };
  }

  // Fallback: rollup table. l3 = robot fully cleared. Backup robots are not
  // in the rollup (per-match only), so this falls back to the roster count
  // — which is correct outside an active inspection window.
  const rollup = state.teamRobotInspection[team]?.[event] ?? {};
  const passed = teamRobots.filter((r) => rollup[r] === 'l3').length;
  return { passed, total };
}

/**
 * True if any of a team's robots for a given match has cleared at least
 * Station 1. Used by the schedule view to lock volunteers out of
 * reverting `at_inspection → not_tracked` once the inspection desk has
 * started working the team — otherwise the team would silently
 * disappear from the inspection queue with progress already on the
 * books. Staff+ retain the full editable set and can still override.
 */
export function teamHasInspectionProgress(
  team: TeamId,
  match: string,
  state: AppState,
): boolean {
  return state.queue.some(
    (q) => q.team === team && q.match === match && q.passedStations.includes('s1'),
  );
}
